/**
 * DaysActiveBadge — Iter. 411.mm18
 * ────────────────────────────────
 * Small "🔥 14 Jours Actifs" badge shown at the top of the Dashboard.
 * Reads days from GET /api/gamification/days-active which prefers
 * `current_streak` and falls back to elapsed days since `created_at`.
 *
 * Design: matches the GLOW aesthetic — flame orange gradient border,
 * amber glow, semi-transparent black glass background. Kept intentionally
 * small so it sits next to the points balance without stealing focus.
 */
import React, { useEffect, useState } from 'react';
import { Flame } from 'lucide-react';
import { api, useLanguage } from '../context/AppContext';

export default function DaysActiveBadge() {
  const { language } = useLanguage();
  const [state, setState] = useState({ days: null, mode: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/gamification/days-active');
        if (cancelled) return;
        const d = res?.data;
        if (typeof d?.days === 'number' && d.days > 0) {
          setState({ days: d.days, mode: d.mode });
        }
      } catch { /* silent — cosmetic surface */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state.days == null) return null;

  const L = (fr, en, es) => language === 'es' ? es : (language === 'en' ? en : fr);
  const isStreak = state.mode === 'streak';
  const dayWord = state.days === 1
    ? L('Jour', 'Day', 'Día')
    : L('Jours', 'Days', 'Días');
  const suffix = isStreak
    ? L('en série', 'streak', 'en serie')
    : L('actifs', 'active', 'activos');

  return (
    <div
      data-testid="days-active-badge"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest select-none"
      style={{
        background: 'linear-gradient(135deg, rgba(251,146,60,0.18) 0%, rgba(0,0,0,0.65) 100%)',
        border: '1px solid rgba(251,146,60,0.5)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 0 12px rgba(251,146,60,0.35), inset 0 1px 0 rgba(255,255,255,0.05)',
        color: '#fed7aa',
        textShadow: '0 0 4px rgba(251,146,60,0.4)',
      }}
      title={isStreak
        ? L(
            `Streak actif — ${state.days} jour${state.days > 1 ? 's' : ''} d'affilée`,
            `Active streak — ${state.days} day${state.days > 1 ? 's' : ''} in a row`,
            `Racha activa — ${state.days} día${state.days > 1 ? 's' : ''} seguidos`)
        : L(
            `${state.days} jour${state.days > 1 ? 's' : ''} depuis ton inscription`,
            `${state.days} day${state.days > 1 ? 's' : ''} since signup`,
            `${state.days} día${state.days > 1 ? 's' : ''} desde el registro`)
      }
    >
      <Flame
        className="w-3.5 h-3.5"
        style={{ color: '#fb923c', filter: 'drop-shadow(0 0 3px rgba(251,146,60,0.8))' }}
        strokeWidth={2.5}
      />
      <span className="tabular-nums" data-testid="days-active-count">{state.days}</span>
      <span>{dayWord}</span>
      <span className="opacity-70">{suffix}</span>
    </div>
  );
}
